import { motion } from "framer-motion";
import { Briefcase, GraduationCap, Lightbulb, Link2, DoorOpen, Activity, Heart } from "lucide-react";

const CASCADE = [
  { label: "ONE IDEA", size: 8, color: "#FF8800" },
  { label: "ONE ENTREPRENEUR", size: 11, color: "#FF8800" },
  { label: "ONE BUSINESS", size: 14, color: "#00B0FF" },
  { label: "CUSTOMERS", size: 17, color: "#00B0FF" },
  { label: "REVENUE", size: 20, color: "#00E676" },
  { label: "EMPLOYEES", size: 24, color: "#00E676" },
  { label: "FAMILIES", size: 28, color: "#FF8800" },
  { label: "COMMUNITIES", size: 33, color: "#00B0FF" },
  { label: "INDUSTRIES", size: 38, color: "#00E676" },
  { label: "A STRONGER INDIA", size: 46, color: "#FF8800" },
];

const CREATES = [
  { icon: Briefcase, label: "Employment" },
  { icon: GraduationCap, label: "Skills" },
  { icon: Lightbulb, label: "Innovation" },
  { icon: Link2, label: "Supply chains" },
  { icon: DoorOpen, label: "Opportunities" },
  { icon: Activity, label: "Economic activity" },
  { icon: Heart, label: "Hope" },
];

export function IdeaCascade() {
  return (
    <section data-testid="idea-cascade-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(255,136,0,0.06),transparent_60%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">Ripple Effect</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="idea-cascade-headline">
            FROM ONE IDEA TO <span className="text-gradient-saffron">MANY OPPORTUNITIES</span>
          </h2>
        </motion.div>

        <div className="mt-16 flex flex-col items-center gap-6" data-testid="idea-cascade">
          {CASCADE.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.55 }}
              className="flex items-center gap-5"
              data-testid={`idea-cascade-step-${i}`}
            >
              <div
                className="rounded-full shrink-0 animate-pulse-node"
                style={{
                  width: c.size,
                  height: c.size,
                  background: c.color,
                  boxShadow: `0 0 ${c.size * 2}px ${c.color}aa, 0 0 ${c.size * 4}px ${c.color}44`,
                }}
              />
              <span
                className={`font-display tracking-[0.1em] ${
                  i === CASCADE.length - 1 ? "font-extrabold text-gradient-india" : "font-semibold text-white"
                }`}
                style={{ fontSize: `${0.85 + i * 0.13}rem` }}
              >
                {c.label}
              </span>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 text-center"
        >
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed" data-testid="idea-cascade-message">
            A successful business does more than generate revenue. It creates:
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3" data-testid="idea-cascade-creates">
            {CREATES.map((c, i) => (
              <motion.span
                key={c.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="glass px-5 py-3 rounded-full flex items-center gap-2.5 text-sm font-medium text-white"
              >
                <c.icon className="w-4 h-4 text-[#FF8800]" />
                {c.label}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
